import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MessageSquareReply } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useSubmitPcmpMarketingExplanation } from '../../hooks/useSubmitPcmpMarketingExplanation';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpCommentField } from './PcmpCommentField';

const explanationFormSchema = z.object({
  explanation: z.string().trim().min(1, 'An explanation is required.').max(PCMP_MKT_COMMENT_MAX),
});

type ExplanationFormValues = z.infer<typeof explanationFormSchema>;

export interface PcmpMarketingExplanationTarget {
  proposalId: number;
  refNo: string;
  /** MIS's Not Verified reason, when the caller has it. */
  misReasonLabel?: string | null | undefined;
}

interface PcmpMarketingExplanationDialogProps {
  target: PcmpMarketingExplanationTarget;
  onClose: () => void;
}

/** Legacy MktExplanationSubmit: Marketing answers MIS's Not Verified, once. */
export function PcmpMarketingExplanationDialog({
  target,
  onClose,
}: PcmpMarketingExplanationDialogProps) {
  const form = useForm<ExplanationFormValues>({
    resolver: zodResolver(explanationFormSchema),
    defaultValues: { explanation: '' },
  });
  const { mutate, isPending, error } = useSubmitPcmpMarketingExplanation(
    target.proposalId,
    onClose,
  );

  const onSubmit = form.handleSubmit((values) => mutate({ explanation: values.explanation }));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Submit Explanation"
        description={target.refNo}
        icon={MessageSquareReply}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Submit Explanation"
        pendingLabel="Submitting..."
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">
            MIS marked this proposal Not Verified
          </span>
          {target.misReasonLabel ? (
            <span className="font-medium">{target.misReasonLabel}</span>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          The explanation goes back to MIS for review. It can be submitted only once.
        </p>
        <PcmpCommentField
          control={form.control}
          name="explanation"
          label="Explanation"
          required
          placeholder="What MIS should know to verify it"
        />
      </PcmpEditDialog>
    </Form>
  );
}
